import type { LiveEventInfo, PlayerInfo } from "../types";

export function str(v: unknown, fb = "0"): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(Math.floor(v));
  if (typeof v === "bigint") return v.toString(10);
  return fb;
}

export function num(v: unknown, fb: number): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fb;
}

function eventLabel(id: string): string {
  if (id === "cookie_rush") return "Cookie Rush";
  if (id === "golden_cookie") return "Golden Cookie";
  if (id === "sneaky_cookie") return "Sneaky Cookie";
  if (id === "mini_cookie") return "Mini Cookie";
  return id;
}

export function normalizeSpec(v: unknown): PlayerInfo["spec"] {
  if (typeof v === "string") {
    const low = v.toLowerCase();
    if (low === "engineer") return "Engineer";
    if (low === "automation") return "Automation";
    if (low === "fortune") return "Fortune";
    if (low === "coordinator") return "Coordinator";
    if (low === "baker") return "Baker";
    if (v === "Baker" || v === "Engineer" || v === "Automation" || v === "Fortune" || v === "Coordinator") {
      return v as PlayerInfo["spec"];
    }
  }
  return "Baker";
}

export function normalizeCosmetics(v: unknown): import("../types").PlayerCosmetics | undefined {
  if (typeof v !== "object" || v === null) return undefined;
  const r = v as Record<string, unknown>;
  const s = (k: string): string => (typeof r[k] === "string" ? (r[k] as string) : "");
  return { skin: s("skin"), glow: s("glow"), ring: s("ring"), trail: s("trail"), sound: s("sound") };
}

export function normalizePlayer(p: Record<string, unknown>): PlayerInfo {
  const userId = typeof p["userId"] === "string" ? (p["userId"] as string) : "unknown";
  const name =
    typeof p["name"] === "string"
      ? (p["name"] as string)
      : typeof p["displayName"] === "string"
        ? (p["displayName"] as string)
        : userId;
  const av = typeof p["avatar"] === "string" ? (p["avatar"] as string) : typeof p["avatarUrl"] === "string" ? (p["avatarUrl"] as string) : null;
  const rawBest = p["bestTesterCps"] ?? p["best_tester_cps"] ?? p["testerCps"];
  const bestTesterCps = typeof rawBest === "number" && Number.isFinite(rawBest) ? rawBest : typeof rawBest === "string" ? num(rawBest, 0) : undefined;
  return {
    userId,
    name,
    spec: normalizeSpec(p["spec"] ?? p["specialization"]),
    color: typeof p["color"] === "string" ? (p["color"] as string) : "#38bdf8",
    active: p["active"] !== false,
    contribution: str(p["contribution"]),
    lastClickAt: num(p["lastClickAt"] ?? p["lastSeen"] ?? p["serverTime"], 0),
    avatar: av,
    autoLevel: num(p["autoLevel"], 0),
    cps: str(p["cps"], "0"),
    clickPower: typeof p["clickPower"] === "string" ? (p["clickPower"] as string) : typeof p["click_power"] === "string" ? (p["click_power"] as string) : undefined,
    level: typeof p["level"] === "number" && Number.isFinite(p["level"]) ? (p["level"] as number) : undefined,
    xp: typeof p["xp"] === "string" ? (p["xp"] as string) : undefined,
    totalClicks: typeof p["totalClicks"] === "string" ? (p["totalClicks"] as string) : typeof p["total_clicks"] === "string" ? (p["total_clicks"] as string) : undefined,
    combo: typeof p["combo"] === "number" && Number.isFinite(p["combo"]) ? (p["combo"] as number) : undefined,
    cosmetics: normalizeCosmetics(p["cosmetics"]),
    bestTesterCps,
  };
}

export function normalizeEvent(e: Record<string, unknown>): LiveEventInfo {
  const id =
    typeof e["id"] === "string"
      ? (e["id"] as string)
      : typeof e["eventId"] === "string"
        ? (e["eventId"] as string)
        : (() => {
            try {
              return crypto.randomUUID();
            } catch {
              return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
            }
          })();
  const mult = num(e["multiplierBps"], 0);
  const bonus =
    typeof e["bonus"] === "string" && (e["bonus"] as string).length > 0
      ? (e["bonus"] as string)
      : mult > 0
        ? "+" + Math.round((mult - 10000) / 100) + "% cookies"
        : "";
  const sx = e["sneakyId"] !== undefined ? e["sneakyId"] : e["sneaky_id"];
  const cx = e["cookieId"] !== undefined ? e["cookieId"] : e["cookie_id"];
  const mx = e["multiplier"];
  const xv = e["x"];
  const yv = e["y"];
  return {
    id,
    kind: typeof e["kind"] === "string" ? (e["kind"] as string) : id,
    label: typeof e["label"] === "string" ? (e["label"] as string) : eventLabel(id),
    endsAt: num(e["endsAt"] ?? e["expiresAt"], Date.now() + 20000),
    bonus,
    x: typeof xv === "number" ? xv : typeof xv === "string" ? num(xv, 0.5) : undefined,
    y: typeof yv === "number" ? yv : typeof yv === "string" ? num(yv, 0.5) : undefined,
    sneakyId: typeof sx === "string" && sx !== "" ? sx : undefined,
    cookieId: typeof cx === "string" && cx !== "" ? cx : undefined,
    multiplier: typeof mx === "number" && Number.isFinite(mx) ? Math.max(2, Math.min(20, Math.floor(mx))) : undefined,
  };
}

export function normalizeLeaderboardEntry(p: Record<string, unknown>): import("../types").LeaderboardEntry {
  const userId = typeof p["userId"] === "string" ? (p["userId"] as string) : "unknown";
  const name = typeof p["name"] === "string" ? (p["name"] as string) : userId;
  const av = typeof p["avatar"] === "string" ? (p["avatar"] as string) : null;
  const rawBest = p["bestTesterCps"] ?? p["best_tester_cps"] ?? p["testerCps"];
  const bestTesterCps = typeof rawBest === "number" && Number.isFinite(rawBest) ? rawBest : typeof rawBest === "string" ? num(rawBest, 0) : 0;
  return {
    userId,
    name,
    avatar: av,
    color: typeof p["color"] === "string" ? (p["color"] as string) : "#38bdf8",
    spec: normalizeSpec(p["spec"] ?? p["specialization"]),
    specialization: typeof p["specialization"] === "string" ? (p["specialization"] as string) : typeof p["spec"] === "string" ? (p["spec"] as string) : "baker",
    contribution: str(p["contribution"]),
    totalClicks: str(p["totalClicks"] ?? p["total_clicks"], "0"),
    level: num(p["level"], 1),
    cps: str(p["cps"], "0"),
    clickPower: str(p["clickPower"] ?? p["click_power"], "1"),
    active: p["active"] === true,
    cosmetics: normalizeCosmetics(p["cosmetics"]),
    bestTesterCps,
  };
}

export function normalizeSnapshot(d: Record<string, unknown>): import("../types").Snapshot | null {
  const b = d["bakery"] as Record<string, unknown> | undefined;
  const m = d["me"] as Record<string, unknown> | null | undefined;
  if (!b) return null;
  const upgradesRaw = d["upgrades"];
  const modulesRaw = d["modules"];
  const projectsRaw = d["projects"];
  const questsRaw = d["quests"];
  const abilitiesRaw = d["abilities"];
  const cosmeticsRaw = d["cosmetics"];
  const offlineRaw = d["offlineBonus"] as Record<string, unknown> | null | undefined;
  const meUpgrades: Record<string, number> = {};
  if (m !== null && m !== undefined && typeof m["upgrades"] === "object" && m["upgrades"] !== null) {
    for (const [k, v] of Object.entries(m["upgrades"] as Record<string, unknown>)) meUpgrades[k] = num(v, 0);
  }
  const guildRaw = d["guild"] as Record<string, unknown> | undefined;
  const guildName = guildRaw !== undefined && typeof guildRaw["name"] === "string" && (guildRaw["name"] as string) !== "" ? (guildRaw["name"] as string) : null;
  const guildId = guildRaw !== undefined && typeof guildRaw["id"] === "string" ? (guildRaw["id"] as string) : "";
  const guildIcon = guildRaw !== undefined && typeof guildRaw["icon"] === "string" ? (guildRaw["icon"] as string) : null;
  return {
    seq: num(d["seq"], 0),
    serverTime: num(d["serverTime"], Date.now()),
    guild: guildName !== null ? { id: guildId, name: guildName, icon: guildIcon } : null,
    bakery: {
      cookies: str(b["cookies"]),
      cps: str(b["cps"] ?? b["cookiesPerSecond"]),
      level: num(b["level"], 1),
      status: typeof b["status"] === "string" ? (b["status"] as string) : undefined,
      activeBakers: num(b["activeBakers"], 0),
      nextLevelCookies: str(b["nextLevelCookies"] ?? b["next_level_cookies"], "10000"),
      levelProgress: num(b["levelProgress"] ?? b["level_progress"], 0),
      lifetimeCookies: str(b["lifetimeCookies"] ?? b["lifetime_cookies"] ?? b["cookies"], "0"),
    },
    me: {
      credits: str(m?.["credits"]),
      clickPower: str(m?.["clickPower"], "1"),
      contribution: str(m?.["contribution"], "0"),
      upgrades: meUpgrades,
      combo: num(m?.["combo"], 0),
      totalClicks: str(m?.["totalClicks"] ?? m?.["total_clicks"], "0"),
      level: num(m?.["level"], 1),
      xp: str(m?.["xp"], str(m?.["totalClicks"] ?? m?.["total_clicks"], "0")),
      nextLevelXp: str(m?.["nextLevelXp"] ?? m?.["next_level_xp"], "50"),
      specialization: typeof m?.["specialization"] === "string" ? (m["specialization"] as string) : typeof m?.["spec"] === "string" ? (m["spec"] as string) : "baker",
      lastSpecLevel: num(m?.["lastSpecLevel"] ?? m?.["last_spec_level"], 0),
      cosmetics: (() => {
        const c = m?.["cosmetics"] as Record<string, unknown> | undefined;
        if (!c || typeof c !== "object") return undefined;
        const owned = Array.isArray(c["owned"]) ? (c["owned"] as unknown[]).filter((x): x is string => typeof x === "string") : [];
        const eq = normalizeCosmetics(c["equipped"]) ?? { skin: "", glow: "", ring: "", trail: "", sound: "" };
        return { owned, equipped: eq };
      })(),
    },
    players: Array.isArray(d["players"])
      ? (d["players"] as unknown[])
          .filter((p) => p && typeof p === "object")
          .map((p) => normalizePlayer(p as Record<string, unknown>))
      : [],
    leaderboard: Array.isArray(d["leaderboard"])
      ? (d["leaderboard"] as unknown[])
          .filter((p) => p && typeof p === "object")
          .map((p) => normalizeLeaderboardEntry(p as Record<string, unknown>))
      : [],
    events: Array.isArray(d["events"])
      ? (d["events"] as unknown[])
          .filter((e) => e && typeof e === "object")
          .map((e) => normalizeEvent(e as Record<string, unknown>))
      : [],
    upgrades: Array.isArray(upgradesRaw)
      ? (upgradesRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          name: typeof u["name"] === "string" ? (u["name"] as string) : "",
          kind: typeof u["kind"] === "string" ? (u["kind"] as string) : "",
          baseCost: str(u["baseCost"]),
          maxLevel: num(u["maxLevel"], 99),
          level: num(u["level"], 0),
          cost: str(u["cost"]),
          locked: Boolean(u["locked"]),
          disabled: Boolean(u["disabled"]),
          unlockBakeryLevel: num(u["unlockBakeryLevel"] ?? u["unlockLevel"], 1),
          specLocked: typeof u["specLocked"] === "string" ? (u["specLocked"] as string) : "",
          requiresId: typeof u["requiresId"] === "string" ? (u["requiresId"] as string) : "",
          requiresLevel: num(u["requiresLevel"], 0),
        }))
      : undefined,
    modules: Array.isArray(modulesRaw)
      ? (modulesRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          name: typeof u["name"] === "string" ? (u["name"] as string) : "",
          baseCps: str(u["baseCps"]),
          level: num(u["level"], 0),
          maxLevel: num(u["maxLevel"], 99),
          locked: Boolean(u["locked"]),
          cost: str(u["cost"]),
          requiredBakeryLevel: num(u["requiredBakeryLevel"] ?? u["lockedLevel"], 1),
          requiresId: typeof u["requiresId"] === "string" ? (u["requiresId"] as string) : "",
          requiresLevel: num(u["requiresLevel"], 0),
        }))
      : undefined,
    projects: Array.isArray(projectsRaw)
      ? (projectsRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          name: typeof u["name"] === "string" ? (u["name"] as string) : "",
          description: typeof u["description"] === "string" ? (u["description"] as string) : "",
          cookieCost: str(u["cookieCost"]),
          cpsBonus: str(u["cpsBonus"]),
          status: typeof u["status"] === "string" ? (u["status"] as string) : "",
          progress: str(u["progress"]),
          requiredBakeryLevel: num(u["requiredBakeryLevel"], 1),
          durationMs: num(u["durationMs"], 0),
          cooldownMs: num(u["cooldownMs"], 0),
          exclusiveGroup: typeof u["exclusiveGroup"] === "string" ? (u["exclusiveGroup"] as string) : "",
          requiresProjectId: typeof u["requiresProjectId"] === "string" ? (u["requiresProjectId"] as string) : "",
          requiresAny: Array.isArray(u["requiresAny"]) ? (u["requiresAny"] as unknown[]).filter((x): x is string => typeof x === "string") : [],
          cooldownRemainingMs: num(u["cooldownRemainingMs"], 0),
        }))
      : undefined,
    quests: Array.isArray(questsRaw)
      ? (questsRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          target: u["target"] === undefined ? undefined : num(u["target"], 0),
          reward: u["reward"] === undefined ? undefined : num(u["reward"], 0),
          progress: num(u["progress"], 0),
          claimed: Boolean(u["claimed"]),
        }))
      : undefined,
    abilities: Array.isArray(abilitiesRaw)
      ? (abilitiesRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          name: typeof u["name"] === "string" ? (u["name"] as string) : "",
          description: typeof u["description"] === "string" ? (u["description"] as string) : "",
          cost: str(u["cost"], "0"),
          cooldownMs: num(u["cooldownMs"], 0),
          cooldownRemainingMs: num(u["cooldownRemainingMs"], 0),
          activeRemainingMs: num(u["activeRemainingMs"], 0),
          endsAt: typeof u["endsAt"] === "number" && Number.isFinite(u["endsAt"] as number) ? (u["endsAt"] as number) : null,
          locked: Boolean(u["locked"]),
          lockReason: typeof u["lockReason"] === "string" ? (u["lockReason"] as string) : undefined,
          specLocked: typeof u["specLocked"] === "string" ? (u["specLocked"] as string) : "",
          unlockBakerLevel: num(u["unlockBakerLevel"], 1),
          durationMs: num(u["durationMs"], 0),
        }))
      : undefined,
    offlineBonus: offlineRaw && typeof offlineRaw["cookies"] === "string"
      ? { cookies: str(offlineRaw["cookies"]), credits: str(offlineRaw["credits"]), hours: num(offlineRaw["hours"], 0), rate: num(offlineRaw["rate"], 0) }
      : null,
    cosmetics: Array.isArray(cosmeticsRaw)
      ? (cosmeticsRaw as Record<string, unknown>[]).map((u) => ({
          id: typeof u["id"] === "string" ? (u["id"] as string) : "",
          name: typeof u["name"] === "string" ? (u["name"] as string) : "",
          kind: typeof u["kind"] === "string" ? (u["kind"] as string) : "",
          currency: typeof u["currency"] === "string" ? (u["currency"] as string) : "credits",
          cost: str(u["cost"], "0"),
          description: typeof u["description"] === "string" ? (u["description"] as string) : "",
          owned: Boolean(u["owned"]),
          equipped: Boolean(u["equipped"]),
        }))
      : undefined,
  };
}
